import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import DashNav from "@/components/chalann/DashNav";
import { listProducts, createProduct, updateProduct, deleteProduct, formatMXN, getStore } from "@/lib/api";
import InventarioOnboarding from "@/components/chalann/InventarioOnboarding";
import BarcodeScanModal from "@/components/chalann/BarcodeScanModal";
import { Plus, Search, AlertTriangle, Trash2, X, Package, Sparkles, Zap, Boxes, Library, ScanLine } from "lucide-react";
import { toast, Toaster } from "sonner";
import AIProductModal from "@/components/chalann/AIProductModal";
import PriceDictationModal from "@/components/chalann/PriceDictationModal";
import RestockModal from "@/components/chalann/RestockModal";
import { KeyRound } from "lucide-react";

const emptyForm = {
  name: "", category: "General", sale_price: "", cost_price: "",
  stock_quantity: "", min_stock_alert: "0", barcode: "", wholesale_provider: "", unit: "pza",
};

export default function Inventario() {
  const nav = useNavigate();
  const store = getStore();
  const [products, setProducts] = useState([]);
  const [q, setQ] = useState("");
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [showAI, setShowAI] = useState(false);
  const [showPrices, setShowPrices] = useState(false);
  const [showRestock, setShowRestock] = useState(false);
  const [showScan, setShowScan] = useState(false);
  const [form, setForm] = useState(emptyForm);
  const [editingId, setEditingId] = useState(null);

  useEffect(() => {
    if (!store?.id) return nav("/setup");
    load();
  }, [nav, store?.id]);

  const load = async () => {
    setLoading(true);
    try {
      const list = await listProducts();
      setProducts(list);
    } catch (e) {
      toast.error("No se pudo cargar el inventario.");
    } finally { setLoading(false); }
  };

  const openNew = () => { setForm(emptyForm); setEditingId(null); setShowForm(true); };
  const noPriceCount = products.filter((p) => (p.sale_price ?? 0) <= 0).length;
  const openEdit = (p) => {
    setForm({
      name: p.name, category: p.category, sale_price: p.sale_price, cost_price: p.cost_price,
      stock_quantity: p.stock_quantity, min_stock_alert: p.min_stock_alert,
      barcode: p.barcode || "", wholesale_provider: p.wholesale_provider || "", unit: p.unit || "pza",
    });
    setEditingId(p.id);
    setShowForm(true);
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) return toast.error("Ponle nombre al producto");
    if (!form.sale_price) return toast.error("Falta el precio de venta");
    const payload = {
      ...form,
      sale_price: Number(form.sale_price),
      cost_price: Number(form.cost_price || 0),
      stock_quantity: Number(form.stock_quantity || 0),
      min_stock_alert: Number(form.min_stock_alert || 0),
      barcode: form.barcode || null,
      wholesale_provider: form.wholesale_provider || null,
    };
    try {
      if (editingId) {
        await updateProduct(editingId, payload);
        toast.success("Producto actualizado");
      } else {
        await createProduct(payload);
        toast.success(`${form.name} agregado al inventario`);
      }
      setShowForm(false);
      load();
    } catch {
      toast.error("Algo salió mal, intenta de nuevo");
    }
  };

  const remove = async (p) => {
    if (!window.confirm(`¿Eliminar ${p.name}?`)) return;
    try {
      await deleteProduct(p.id);
      toast.success("Eliminado");
      load();
    } catch { toast.error("No se pudo eliminar"); }
  };

  const filtered = products.filter((p) => {
    const s = q.toLowerCase();
    return !s || p.name.toLowerCase().includes(s) || (p.barcode || "").includes(s) || (p.wholesale_provider || "").toLowerCase().includes(s);
  });

  return (
    <main className="min-h-screen bg-[#050505] text-white" data-testid="inventario">
      <div className="grain-overlay" />
      <DashNav />

      <div className="relative max-w-[1600px] mx-auto px-6 md:px-10 py-8 md:py-12">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4 mb-8">
          <div>
            <p className="font-mono text-[11px] tracking-[0.3em] text-white/40 uppercase mb-2">/ Inventario</p>
            <h1 className="font-display text-4xl md:text-6xl tracking-tighter leading-none">
              Lo que hay <span className="text-[#ccff00]">en tu tienda.</span>
            </h1>
          </div>
          <div className="flex flex-wrap gap-3">
            <button
              onClick={() => setShowScan(true)}
              className="inline-flex items-center gap-2 px-4 py-2.5 rounded-full bg-[#CCFF00] text-black hover:bg-white font-bold text-sm transition"
              data-testid="btn-scan"
              title="Escanear código de barras"
            >
              <ScanLine size={16} /> Escanear
            </button>
            <button className="btn-ghost" onClick={openNew} data-testid="btn-nuevo-producto">
              <Plus size={16} /> Nuevo producto
            </button>
            <button
              onClick={() => nav("/catalogo")}
              className="inline-flex items-center gap-2 px-4 py-2.5 rounded-full bg-white text-black hover:bg-white/90 font-bold text-sm"
              data-testid="btn-catalogo"
            >
              <Library size={16} strokeWidth={2.5} /> Catálogo maestro
            </button>
            <button
              onClick={() => setShowRestock(true)}
              className="inline-flex items-center gap-2 px-4 py-2.5 rounded-full bg-[#00b3ff] hover:bg-[#33c4ff] text-black font-bold text-sm transition-colors duration-200 shadow-[0_10px_30px_-10px_rgba(0,179,255,0.55)]"
              data-testid="btn-meter-inventario"
            >
              <Boxes size={16} strokeWidth={2.5} /> Meter al inventario (Voz)
            </button>
            <button
              className="btn-primary relative overflow-hidden"
              onClick={() => setShowAI(true)}
              data-testid="btn-ai-producto"
            >
              <Sparkles size={16} /> Agregar con IA
            </button>
            <button
              onClick={() => setShowPrices(true)}
              className="inline-flex items-center gap-2 px-4 py-2.5 rounded-full bg-[#ccff00] hover:bg-[#dfff40] text-black font-bold text-sm transition-colors duration-200 shadow-[0_10px_30px_-10px_rgba(204,255,0,0.55)]"
              data-testid="btn-precios-dia"
            >
              <Zap size={16} strokeWidth={2.5} /> Actualizar Precios del Día (Voz)
            </button>
          </div>
        </div>

        {noPriceCount > 0 && (
          <button
            onClick={() => nav("/precios-iniciales")}
            className="w-full mb-6 flex items-center justify-between gap-4 px-5 py-4 rounded-2xl border border-[#FF5A00]/40 bg-[#FF5A00]/[0.06] hover:bg-[#FF5A00]/[0.10] transition text-left"
            data-testid="banner-needs-pricing"
          >
            <div className="flex items-start gap-3">
              <KeyRound size={20} className="text-[#FF5A00] shrink-0 mt-0.5" />
              <div>
                <p className="font-satoshi text-base leading-tight">
                  Tienes <span className="text-[#FF5A00] font-bold">{noPriceCount}</span> {noPriceCount === 1 ? "producto sin precio" : "productos sin precio"}
                </p>
                <p className="text-white/50 text-xs mt-1 font-mono uppercase tracking-widest">
                  Captúralos con el teclado · Después la voz actualiza cambios
                </p>
              </div>
            </div>
            <span className="font-mono text-[10px] tracking-widest text-[#FF5A00] uppercase whitespace-nowrap">
              Ponerles precio →
            </span>
          </button>
        )}

        <div className="flex flex-col md:flex-row md:items-center gap-3 mb-6">
          <div className="flex-1 flex items-center gap-2 bg-white/[0.02] border border-white/10 rounded-full px-4 py-3">
            <Search size={16} className="text-white/40" />
            <input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Buscar por nombre, código o proveedor…"
              className="flex-1 bg-transparent outline-none text-sm placeholder-white/30"
              data-testid="inv-search"
            />
          </div>
          <p className="font-mono text-xs text-white/40 tracking-widest md:ml-3">
            {filtered.length} de {products.length} productos
          </p>
        </div>

        {!loading && products.length === 0 ? (
          <InventarioOnboarding
            savedCount={products.length}
            onDone={() => load()}
            onFinish={() => { toast.success("¡Listo! Tu inventario ya puede vender."); load(); }}
          />
        ) : (
        <div className="rounded-2xl border border-white/10 overflow-hidden">
          <div className="grid grid-cols-12 gap-2 px-4 md:px-6 py-3 bg-white/[0.03] font-mono text-[10px] tracking-widest text-white/40 uppercase">
            <div className="col-span-5 md:col-span-4">Producto</div>
            <div className="col-span-2 hidden md:block">Categoría</div>
            <div className="col-span-2 hidden md:block">Proveedor</div>
            <div className="col-span-3 md:col-span-2 text-right">Precio</div>
            <div className="col-span-3 md:col-span-1 text-right">Stock</div>
            <div className="col-span-1 text-right">·</div>
          </div>

          {loading ? (
            <EmptyRow text="Cargando…" />
          ) : filtered.length === 0 ? (
            <EmptyState onNew={openNew} hasAny={products.length > 0} />
          ) : (
            filtered.map((p) => {
              const low = p.min_stock_alert > 0 && p.stock_quantity <= p.min_stock_alert;
              return (
                <div
                  key={p.id}
                  className={`grid grid-cols-12 gap-2 px-4 md:px-6 py-3 border-t border-white/5 transition-colors items-center ${
                    low
                      ? "bg-[#ff5a5a]/[0.10] hover:bg-[#ff5a5a]/[0.16] border-t-[#ff5a5a]/25"
                      : "hover:bg-white/[0.02]"
                  }`}
                  data-testid={`inv-row-${p.id}`}
                >
                  <button className="col-span-5 md:col-span-4 text-left" onClick={() => openEdit(p)}>
                    <div className="flex items-center gap-2 flex-wrap">
                      <p className="font-medium text-sm truncate">{p.name}</p>
                      {low && (
                        <span
                          className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-[#ff5a5a] text-black text-[9px] font-mono uppercase tracking-widest font-bold"
                          data-testid={`low-badge-${p.id}`}
                        >
                          <AlertTriangle size={9} strokeWidth={2.5} /> ¡Por agotarse!
                        </span>
                      )}
                    </div>
                    <p className="text-[11px] text-white/40 font-mono">
                      {p.barcode ? `#${p.barcode}` : "sin código"}
                    </p>
                  </button>
                  <div className="col-span-2 hidden md:block text-sm text-white/70">{p.category}</div>
                  <div className="col-span-2 hidden md:block text-sm text-white/60">{p.wholesale_provider || "—"}</div>
                  <div className="col-span-3 md:col-span-2 text-right font-mono">{formatMXN(p.sale_price)}</div>
                  <div className={`col-span-3 md:col-span-1 text-right font-mono ${low ? "text-[#ff5a5a] font-bold" : ""}`}>
                    {p.stock_quantity} <span className="text-white/40 text-[10px]">{p.unit}</span>
                  </div>
                  <div className="col-span-1 text-right">
                    <button onClick={() => remove(p)} className="text-white/40 hover:text-[#ff5a00] p-1" data-testid={`inv-del-${p.id}`}>
                      <Trash2 size={14} />
                    </button>
                  </div>
                </div>
              );
            })
          )}
        </div>
        )}
      </div>

      <AnimatePresence>
        {showForm && (
          <motion.div
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-center justify-center p-4"
            onClick={() => setShowForm(false)}
          >
            <motion.form
              onSubmit={submit}
              initial={{ y: 30, opacity: 0 }} animate={{ y: 0, opacity: 1 }} exit={{ y: 30, opacity: 0 }}
              transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
              onClick={(e) => e.stopPropagation()}
              className="relative w-full max-w-2xl bg-[#0a0a0a] border border-white/10 rounded-3xl p-6 md:p-10"
              data-testid="product-form"
            >
              <button type="button" onClick={() => setShowForm(false)} className="absolute top-4 right-4 text-white/50 hover:text-white p-2" data-testid="product-form-close">
                <X size={18} />
              </button>
              <div className="chip mb-4"><Package size={12} /> {editingId ? "Editar producto" : "Nuevo producto"}</div>
              <h2 className="font-display text-3xl md:text-4xl tracking-tighter mb-6">
                {editingId ? "Actualiza los datos" : "Agrega al inventario"}
              </h2>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <Field label="Nombre" value={form.name} onChange={(v) => setForm({ ...form, name: v })} placeholder="Coca Cola 600 ml" required testid="pf-name" />
                <Field label="Categoría" value={form.category} onChange={(v) => setForm({ ...form, category: v })} placeholder="Refrescos" testid="pf-cat" />
                <Field label="Precio venta MXN" type="number" step="0.01" value={form.sale_price} onChange={(v) => setForm({ ...form, sale_price: v })} placeholder="22" required testid="pf-price" />
                <Field label="Costo MXN" type="number" step="0.01" value={form.cost_price} onChange={(v) => setForm({ ...form, cost_price: v })} placeholder="15" testid="pf-cost" />
                <Field label="Stock inicial" type="number" step="0.01" value={form.stock_quantity} onChange={(v) => setForm({ ...form, stock_quantity: v })} placeholder="24" testid="pf-stock" />
                <Field label="Alerta stock mínimo" type="number" step="0.01" value={form.min_stock_alert} onChange={(v) => setForm({ ...form, min_stock_alert: v })} placeholder="3" testid="pf-min" />
                <Field label="Código de barras / QR" value={form.barcode} onChange={(v) => setForm({ ...form, barcode: v })} placeholder="7501055363057" testid="pf-barcode" />
                <Field label="Proveedor mayorista" value={form.wholesale_provider} onChange={(v) => setForm({ ...form, wholesale_provider: v })} placeholder="Coca-Cola FEMSA" testid="pf-provider" />
                <div>
                  <label className="font-mono text-[10px] tracking-widest text-white/40 uppercase">Unidad</label>
                  <div className="mt-2 flex flex-wrap gap-2">
                    {["pza", "kg", "lt", "caja"].map((u) => (
                      <button key={u} type="button" onClick={() => setForm({ ...form, unit: u })}
                        className={`px-3 py-1.5 rounded-full text-xs border ${form.unit === u ? "bg-white text-black border-white" : "border-white/10 text-white/70"}`}
                        data-testid={`pf-unit-${u}`}
                      >{u}</button>
                    ))}
                  </div>
                </div>
              </div>

              <div className="mt-8 flex flex-wrap gap-3">
                <button type="submit" className="btn-primary" data-testid="pf-submit">
                  {editingId ? "Guardar cambios" : "Agregar al inventario"}
                </button>
                <button type="button" onClick={() => setShowForm(false)} className="btn-ghost" data-testid="pf-cancel">Cancelar</button>
              </div>
            </motion.form>
          </motion.div>
        )}
      </AnimatePresence>
      <AIProductModal
        open={showAI}
        onClose={() => setShowAI(false)}
        onCreated={load}
      />
      <PriceDictationModal
        open={showPrices}
        onClose={() => setShowPrices(false)}
        onApplied={load}
      />
      <RestockModal
        open={showRestock}
        onClose={() => setShowRestock(false)}
        onApplied={load}
      />
      <BarcodeScanModal
        open={showScan}
        onClose={() => setShowScan(false)}
        onSaved={load}
      />
      <Toaster theme="dark" position="top-center" />
    </main>
  );
}

function Field({ label, value, onChange, placeholder, type = "text", step, required, testid }) {
  return (
    <div>
      <label className="font-mono text-[10px] tracking-widest text-white/40 uppercase">{label}</label>
      <input
        type={type} step={step} value={value} onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder} required={required}
        data-testid={testid}
        className="w-full mt-2 bg-black/50 border border-white/10 rounded-xl px-4 py-3 text-white placeholder-white/30 focus:outline-none focus:border-[#ff5a00] transition-colors"
      />
    </div>
  );
}

function EmptyRow({ text }) {
  return <div className="px-6 py-10 text-center text-white/40 text-sm">{text}</div>;
}

function EmptyState({ onNew, hasAny }) {
  return (
    <div className="px-6 py-16 text-center">
      <p className="font-display text-2xl md:text-3xl tracking-tighter mb-2">
        {hasAny ? "No hay coincidencias." : "Tu inventario está en blanco."}
      </p>
      <p className="text-white/50 text-sm mb-6">
        {hasAny ? "Prueba con otra búsqueda." : "Agrega tu primer producto para empezar a cobrar."}
      </p>
      {!hasAny && <button className="btn-primary" onClick={onNew} data-testid="empty-nuevo"><Plus size={16} /> Agregar primer producto</button>}
    </div>
  );
}
